import { type FC, type SyntheticEvent, useEffect, useRef, useState, useCallback } from "react";
import { Header, type TabProps } from "semantic-ui-react";
import { type StripoInitOpts } from "../../../lib/stripo";
import cn from "classnames";

import Tabs from "../../tabs/Tabs";
import { EditorControls } from "./EditorControls";
import { useEmailEditor } from "../hooks/useEmailEditor";
import { type IEmailEditorController } from "./emailEditorController";
import NotifySettingsForm, { type EmailNotificationFormProps } from "../NotifySettingsForm/EmailNotificationForm";
import { SubjectPreviewControl } from "./SubjectPreviewControl";
import { datadogRum } from "@datadog/browser-rum";

import editorTabsStyles from "../editorTabs.module.scss";
import "./emailEditor.scss";

export interface EmailEditorProps {
  opts: StripoInitOpts;
  onLoaded(controller: IEmailEditorController): void;
  form: EmailNotificationFormProps;
  onDirtyChange?: (dirty: boolean) => void;
}

const PREVIEW_TAB_INDEX = 1;

export const EmailEditor: FC<EmailEditorProps> = ({ onLoaded, opts, form, onDirtyChange }) => {
  const { settingsId, previewId } = opts;
  const [isLoaded, controller, sasUrl, emailPreview] = useEmailEditor({ ...opts });
  const [iframeHeight, setIframeHeight] = useState<string>("0");
  const [iFrameLoaded, setIFrameLoaded] = useState(false);
  const [contentFullyLoaded, setContentFullyLoaded] = useState(false);

  const iFrameRef = useRef<HTMLIFrameElement>(null);

  const handleIframeLoad = useCallback(() => {
    if (!emailPreview) return;
    setIFrameLoaded(true);

    try {
      const iframeWindow = iFrameRef.current?.contentWindow;
      const iframeDocument = iframeWindow?.document;

      // Other condition is handled by the useEffect below
      if (iframeDocument?.readyState === "complete") {
        setIframeHeight(iframeDocument?.body?.scrollHeight.toString() || "0");
        setContentFullyLoaded(true);
      }
    } catch (error) {
      console.error("Error accessing iframe content:", error);
      setContentFullyLoaded(true);
    }
  }, [emailPreview]);

  // Manage iframe load event
  useEffect(() => {
    try {
      const iframeWindow = iFrameRef.current?.contentWindow;

      if (iFrameLoaded && iframeWindow && !contentFullyLoaded) {
        const handleLoad = () => {
          setContentFullyLoaded(true);
          const iframeDocument = iframeWindow?.document;
          setIframeHeight(iframeDocument?.body?.scrollHeight.toString() || "0");
        };

        iframeWindow.addEventListener("load", handleLoad);

        return () => {
          iframeWindow.removeEventListener("load", handleLoad);
        };
      }
    } catch (err) {
      console.error("Error accessing iframe content:", err);
      setContentFullyLoaded(true);
    }
  }, [iFrameLoaded, contentFullyLoaded]);

  useEffect(() => {
    datadogRum.addAction("On EmailEditor useEffect", {
      isLoaded,
      controller,
      sasUrl,
    });

    if (!contentFullyLoaded || !controller || !sasUrl || !iFrameLoaded) return;

    onLoaded(controller);
  }, [isLoaded, contentFullyLoaded, controller, sasUrl, iFrameLoaded, onLoaded]);

  const handleTabChange = async (_: SyntheticEvent, data: TabProps) => {
    const isNotPreviewTab = data.activeIndex !== PREVIEW_TAB_INDEX;

    if (isNotPreviewTab || !isLoaded) {
      return;
    }

    controller?.renderPreview();
  };

  return (
    <div className="email-editor">
      <Tabs
        className={cn(editorTabsStyles["editor-tabs"], "email")}
        renderActiveOnly={false}
        onTabChange={handleTabChange}
        defaultActiveIndex={PREVIEW_TAB_INDEX}
      >
        <Tabs.Pane label="Edit">
          <NotifySettingsForm {...form} onDirtyChange={onDirtyChange} />
          <Header>Content Body</Header>
          <div className="edit-pane">
            <div id={settingsId} className="settings" />
            <div className="editor-area">
              <EditorControls />
              <label className="editor-label">Email Body</label>
              <div id={previewId} className="edit" />
            </div>
          </div>
        </Tabs.Pane>
        <Tabs.Pane label="Preview">
          <SubjectPreviewControl subject={form.subject} previewText={form.previewText} />
          <iframe
            ref={iFrameRef}
            height={iframeHeight}
            className="preview"
            title="Stripo Email Editor"
            srcDoc={emailPreview}
            onLoad={handleIframeLoad}
          />
        </Tabs.Pane>
      </Tabs>
    </div>
  );
};
